import { BaseClass } from '@components/BaseClass.js';
import { ReplaySubject, takeUntil, filter, Subject, Observable, of, throwError } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { DataDict } from '@components/DataDict.js';
import DIContainer from '@components/DependencyInjectionContainer.js';
import { capitalizeFirstLetter, forEachKey, isArray } from '@components/Utility/index.js';
/**
 * Represents a single data record.
 */
export class DataRecord extends BaseClass {
    get loadedData() {
        return { ...this.data, ...this._loadedData };
    }
    get changeSubject() {
        if (!this._changeSubject) {
            this._changeSubject = new Subject();
        }
        return this._changeSubject;
    }
    set changeSubject(subject) {
        if (this._changeSubject) {
            this._changeSubject.complete();
            console.warn('Record already has a change subject, did you intend to overwrite it? Manually setting the change subject is for complex code only to optimize things like grids.');
        }
        this._changeSubject = subject;
    }
    get deleteFlag() { return this._deleteFlag; }
    set deleteFlag(value) {
        this._propertySet('deleteFlag', value);
    }
    childRecordsGet() {
        // TODO: parentRecord handles scenario of task form MAIN record being the parent record of the top-level rows in the subtask grid.
        // but this function does not handle the scenario of the task form MAIN record returning the top-level rows in the subtask grid. 
        // not sure if that is wise or feasible. Currently not required, so can implement if/when needed.
        if (this.listMixinGlobalId) {
            return DIContainer.getInstance('ComponentRegistry').getComponent(this.listMixinGlobalId)?.childRecordsGet(this);
        }
        else {
            return [];
        }
    }
    parentRecordGet(pk) {
        if (this.listMixinGlobalId) {
            const listMixin = DIContainer.getInstance('ComponentRegistry').getComponent(this.listMixinGlobalId);
            if (!pk) {
                return listMixin?.parentRecordGet(this, true);
            }
            else {
                const recordId = listMixin.propertyGet('parentIdMap')?.get(pk);
                if (recordId) {
                    return listMixin.getOptionById(recordId);
                }
                else if (listMixin.record?.pkValue === pk) {
                    return listMixin.record;
                }
            }
        }
    }
    listRecordsGet(excludeThis = false) {
        if (this.listMixinGlobalId) {
            const listMixin = DIContainer.getInstance('ComponentRegistry').getComponent(this.listMixinGlobalId);
            if (excludeThis) {
                return (listMixin?.optionData || []).filter(r => r !== this);
            }
            else {
                return (listMixin?.optionData || [this]);
            }
        }
        else {
            if (excludeThis) {
                return [];
            }
            return [this];
        }
    }
    lockRecord$(initiator, breakLock = true) {
        if (this.recordLocking === 'Y' && this.tableName && this.pkValue) {
            return DIContainer.getInstance('Request').POST({
                endpoint: 'record',
                action: 'lock',
                initiator,
                requestData: {
                    tablename: this.tableName,
                    primarykey: this.pkValue,
                    loaddate: this.get('moddate')
                },
                showErrors: false
            }).pipe(map((response) => {
                this.locked = true;
                return response.payload.outvars;
            }), catchError((error) => {
                const referenceCode = error._error?.[0]?.referencecode || '';
                const message = error._error?.[0]?.errormessage || '';
                if (breakLock
                    && ((DIContainer.getInstance('ConditionsManager').checkPerm('recordlock_exec')
                        && referenceCode.toLowerCase() === 'alreadylockedother')
                        || referenceCode.toLowerCase() === 'alreadylockedself')) {
                    return this.breakLock$(message, initiator);
                }
                else {
                    DIContainer.getInstance('Request').showErrors(error);
                    throw error;
                }
            }));
        }
        else {
            this.locked = false;
            return of({
                isstale: 'N'
            });
        }
    }
    unlockRecord$(initiator, breakLock) {
        if (this.locked || breakLock) {
            return DIContainer.getInstance('Request').POST({
                endpoint: 'record',
                action: 'unlock',
                initiator,
                requestData: {
                    tablename: this.tableName,
                    primarykey: this.pkValue
                },
                showErrors: breakLock
            }).pipe(map((response) => {
                this.locked = false;
                return response;
            }));
        }
        return of(true);
    }
    breakLock$(message, initiator) {
        const modal = DIContainer.new('Alert');
        modal.buttonAdd({
            id: 'breaklock',
            caption: 'Break Lock',
            classList: ['destructive-secondary'],
            index: -1,
            onClick: () => {
                modal.submit(false);
            }
        });
        return modal.open({
            caption: message,
            modalStyle: 'error'
        }).pipe(switchMap((clickedOk) => {
            if (clickedOk) {
                return throwError(() => new Error(message));
            }
            else {
                // do the actual break lock here and get the lock
                return this.unlockRecord$(initiator, true).pipe(switchMap(() => {
                    return this.lockRecord$(initiator, false);
                }));
            }
        }));
    }
    get load$() {
        if (this._load$) {
            return this._load$;
        }
        this._load$ = new ReplaySubject(1);
        if (this.hasLoaded) {
            this._load$.next(true);
        }
        return this._load$;
    }
    get filterModule() { return this._filterModule; }
    set filterModule(value) {
        this._propertySet('filterModule', value);
    }
    get filterExcludeList() { return this._filterExcludeList || []; }
    set filterExcludeList(value) {
        if (typeof value === 'string') {
            value = value.split(',');
        }
        this._propertySet('filterExcludeList', value);
    }
    get progress() { return this._progress; }
    get inProgress() {
        return typeof this._progress !== 'undefined' && this._progress !== 100;
    }
    handleProgress(percent) {
        this._propertySet('progress', percent);
    }
    get pkValue() {
        return this.data[this.pkColumnName] || '';
    }
    get state() { return this._state; }
    set state(value) {
        if (this._state !== value) {
            this._propertySet('state', value);
        }
    }
    /**
     * Creates a new Record instance.
     *
     * @param id - The ID of the new record.
     * @param listMixinGlobalId
     */
    constructor(id, options = {}) {
        super();
        /** The data of the record */
        this.data = {};
        this.metaData = {};
        /** Data that has been changed since the record was last loaded */
        this.changedData = {};
        this._loadedData = {};
        this.defaultData = {};
        this.saveValidationErrors = [];
        /** A set of ReplaySubject instances for each column */
        this.valueSubjects = {};
        this._deleteFlag = 'N';
        /** applicable to records used in the context of a list*/
        this.index = 0; // in grids, index is the visible rows index, so it skips stuff like collapsed groups
        /** Flags indicating if this record passed the conditions for edit/create/add that are stored in tabledef. */
        this.permCreate = true;
        this.permEdit = true;
        this.permDelete = true;
        this.tableName = '';
        this.dataSource = '';
        this.q360formrecordid = '';
        this.pkColumnName = '';
        this.fkColumnName = ''; // this is only for "secondary" record types, and the fkColumnName would need to be a record column in the "primary" record.
        this.recordLocking = ''; // this is only for "primary" record types
        this.defaultState = '';
        this.recordType = '';
        this.dependentColumns = ''; // CSV list 
        this.hasLoaded = false;
        this._filterModule = '';
        this._filterExcludeList = [];
        this._state = 'edit';
        this.id = id;
        this.listMixinGlobalId = options.listMixinGlobalId;
        this.formGlobalId = options.formGlobalId;
    }
    /**
     * Loads the record with the provided data.
     *
     * @param data - The data to load into the record.
     */
    load(data) {
        this.data = data;
        this.changedData = {};
        this._loadedData = {};
        delete this.revertToken;
        if (this.filterModule && this.get('_filterset') === 'Default') {
            this.defaultData = { ...data };
        }
        this.saveValidationErrors.length = 0;
        this.hasLoaded = true;
        this.deleteFlag = 'N';
        for (const columnName in this.valueSubjects) {
            // let val = data ? data[columnName] : '';
            this.valueSubjects[columnName].next({ value: data[columnName], source: 'load' });
        }
        if (this.pkColumnName) {
            if (!data[this.pkColumnName]) {
                this.state = 'none';
            }
            else if (this.defaultState && this.defaultState !== 'none') {
                this.state = this.defaultState;
            }
            else {
                this.state = 'read';
            }
        } /*
        // just causing issues. none should only be used typically for primary/secondary records which would have a PK or detail records which inherit state from their linked record from a list (ie. grid)
        else {
            if (this._state === 'none') {
                this.state = 'read' ;
            }
        }*/
        if (this._load$) {
            this._load$.next(true);
        }
    }
    addChildGridRecordData(tableName, parentId, optionData) {
        this.childGridRecordData = this.childGridRecordData || {};
        this.childGridRecordData[tableName] = this.childGridRecordData[tableName] || {};
        this.childGridRecordData[tableName][parentId] = optionData;
    }
    getChildGridRecordData(tableName, parentId) {
        if (this.childGridRecordData && this.childGridRecordData[tableName] && this.childGridRecordData[tableName][parentId]) {
            return this.childGridRecordData[tableName][parentId];
        }
    }
    deleteChildGridRecordData(tableName, parentId) {
        if (!this.childGridRecordData) {
            return;
        }
        if (parentId && tableName) {
            if (this.childGridRecordData[tableName] && this.childGridRecordData[tableName][parentId]) {
                this.childGridRecordData[tableName][parentId].forEach(record => record.destroy());
                delete this.childGridRecordData[tableName][parentId];
            }
        }
        else if (tableName && !parentId) {
            if (this.childGridRecordData[tableName]) {
                Object.keys(this.childGridRecordData[tableName]).forEach(parentId => {
                    this.deleteChildGridRecordData(tableName, parentId);
                });
                delete this.childGridRecordData[tableName];
            }
        }
        else {
            Object.keys(this.childGridRecordData).forEach(tableName => {
                this.deleteChildGridRecordData(tableName);
                delete this.childGridRecordData;
            });
        }
    }
    addQ360FormRecordValue(column, value, refreshmode) {
        column = column.toLowerCase();
        if (!this.q360formrecordvalue) {
            this.q360formrecordvalue = [];
        }
        let q360FormRecordValue = this.q360formrecordvalue?.find((o) => {
            return (o.columnname || '').toLowerCase() === column;
        });
        if (q360FormRecordValue) {
            q360FormRecordValue.defaultvalue = value;
        }
        else {
            q360FormRecordValue = {
                columnname: column,
                defaultvalue: value,
                q360formno: '',
                recordname: this.id,
                q360formrecordvalueid: '',
                refreshmode: refreshmode || ''
            };
            this.q360formrecordvalue.push(q360FormRecordValue);
        }
    }
    getDefaultValue(column) {
        column = column.toLowerCase();
        return this.q360formrecordvalue?.find((o) => {
            return (o.columnname || '').toLowerCase() === column;
        })?.defaultvalue || undefined;
    }
    /**
     * Returns the value of the specified column.
     *
     * @param columnName - The name of the column.
     * @returns The value of the specified column.
     */
    get(columnName) {
        return this.data[columnName];
    }
    /**
     * Returns an observable that will emit the value of the specified column.
     *
     * @param columnName - The name of the column.
     * @param oOptions - Options for the observable.
     * @returns An observable that will emit the value of the specified column.
     */
    get$(columnName, oOptions = {}) {
        if (!this.valueSubjects[columnName]) {
            const currentValue = this.data[columnName];
            let source = 'load';
            if (Reflect.has(this.changedData, columnName)) {
                source = 'current';
            }
            this.valueSubjects[columnName] = new ReplaySubject(1);
            if (Reflect.has(this.data, columnName)) {
                this.valueSubjects[columnName].next({ value: currentValue, source: source }); // emit current value
            }
        }
        let observable = this.valueSubjects[columnName].asObservable();
        // if you want to ignore loaded data AND distinct, this needs to happen before the filtering or else the first time the value is set it will fire even if it matches the initial loaded value
        if (oOptions.distinct) {
            observable = observable.pipe(distinctUntilChanged((prev, curr) => prev.value === curr.value));
        }
        if (oOptions.ignoreSource) {
            observable = observable.pipe(filter((detail) => {
                if (oOptions.includeInitial) {
                    oOptions.includeInitial = false;
                    return true;
                }
                return oOptions.ignoreSource.indexOf(detail.source) === -1;
            }));
        }
        if (oOptions.includeDetail) {
            return observable.pipe(takeUntil(this.destroy$));
        }
        return observable.pipe(map(x => x.value), takeUntil(this.destroy$));
    }
    /**
     * Forward-looking observable stream of all record changes. Does not have a buffer like get$ so will not fire until an actual changes happens AFTER subscribing.
     * @param oOptions
     */
    allChangesGet$(oOptions = {}) {
        let observable = this.changeSubject.pipe(takeUntil(this.destroy$), filter((change) => change.record === this));
        if (oOptions.ignoreSource) {
            observable = observable.pipe(filter((detail) => oOptions.ignoreSource.indexOf(detail.source) === -1));
        }
        return observable;
    }
    /**
     * Sets the value of the specified column.
     *
     * @param columnName - The name of the column.
     * @param value - The new value.
     * @param source - The source of the change.
     */
    set(columnName, value, source = 'set') {
        const oldValue = this.data[columnName];
        this.data[columnName] = value;
        if (source === 'load') {
            delete this.changedData[columnName];
            delete this._loadedData[columnName];
            if (this.filterModule && this.get('_filterset') === 'Default') {
                this.defaultData[columnName] = value;
            }
        }
        else {
            if (!(columnName in this._loadedData)) {
                this._loadedData[columnName] = oldValue;
            }
            if (this._loadedData[columnName] === value) {
                delete this.changedData[columnName];
                delete this._loadedData[columnName];
            }
            else {
                this.changedData[columnName] = value;
            }
        }
        if (this.valueSubjects[columnName]) {
            this.valueSubjects[columnName].next({ value, source });
        }
        if (this._changeSubject) {
            this._changeSubject.next({ columnName, value, source, record: this, oldValue });
        }
        if (oldValue != value
            && ['currentsync', 'detailsync', 'load'].indexOf(source) === -1
            && this.tableName
            && DIContainer.isSingletonRegistered(capitalizeFirstLetter(this.tableName))) {
            const bizzClass = DIContainer.getInstance(capitalizeFirstLetter(this.tableName));
            bizzClass.programmaticChange(this, columnName, value, oldValue, source);
        }
    }
    metaSet(columnName, key, value) {
        let meta = this.metaData[columnName];
        if (!meta) {
            meta = {};
            this.metaData[columnName] = meta;
        }
        if (typeof value === 'undefined') {
            delete meta[key];
        }
        else {
            meta[key] = value;
        }
    }
    metaGet(columnName) {
        return this.metaData[columnName];
    }
    dataDictByColumnGet(columnName) {
        return DataDict[this.tableName] ? DataDict[this.tableName][columnName] : undefined;
    }
    requestPackageGet(options = {}) {
        let { includeAllColumns = false } = options;
        if (!this.pkValue) {
            // if the record has no PK, presumably it doesnt exist in the db so we will submit the full record.
            includeAllColumns = true;
        }
        return {
            ...(includeAllColumns ? this.data : this.changedData),
            ...{ [this.pkColumnName]: this.pkValue, _id: this.id, _tablename: this.tableName, _pk: this.pkValue, _deleteflag: this._deleteFlag, _editguid: this.data.pg_guid }
        };
    }
    autoSave$(columnName, options) {
        options = options || {};
        return this.get$(columnName, { ignoreSource: ['load'], distinct: true }).pipe(debounceTime(options.debounceTime || 1000), switchMap((newValue) => {
            if (this.pkValue && this.tableName) {
                return DIContainer.getInstance('Request').PATCH({
                    endpoint: 'Record',
                    action: '',
                    requestData: {
                        jsonRequest: JSON.stringify({ griddata: { record: [
                                    {
                                        [this.pkColumnName]: this.pkValue,
                                        _id: this.id,
                                        _tablename: this.tableName,
                                        _pk: this.pkValue,
                                        _deleteflag: this._deleteFlag,
                                        _editguid: this.data.pg_guid,
                                        [columnName]: newValue,
                                        ...(options?.additionalParameters || {})
                                    }
                                ] } }),
                        _tablename: this.tableName
                    }
                });
            }
            else {
                // no pk or table name so we cant auto save
                return of(true);
            }
        }));
    }
    save(context) {
        if (Object.keys(this.changedData).length > 0) {
            if (!this.pkValue && this.state !== 'add') {
                return new Observable(subscriber => {
                    subscriber.error(new Error('Unable to determine primary key and record is not in add mode.'));
                });
            }
            const className = DIContainer.isRegistered(capitalizeFirstLetter(this.tableName)) ? capitalizeFirstLetter(this.tableName) : 'RecordLogic';
            const bizzClass = DIContainer.getInstance(className);
            if (this.state === 'add' && !this.pkValue) {
                // go through the create proc as this record hasnt been pre-created
                return bizzClass.createRecord(context, { _tablename: this.tableName }, [this.data]);
            }
            else {
                return bizzClass.save(context, [this]);
            }
        }
        else {
            return new Observable(subscriber => {
                subscriber.error(new Error('No changes to save.'));
            });
        }
    }
    saveFilters(columnName) {
        const requestData = {
            module: this.filterModule,
            filterSetName: this.get('_filterset')
        };
        requestData.jsonRequest = {};
        requestData.jsonRequest.griddata = {};
        requestData.jsonRequest.griddata.filterdef = [];
        if (columnName) {
            if (!isArray(columnName))
                columnName = [columnName];
            requestData.replaceexistingfilterset = 'N';
        }
        forEachKey(this.data, (value, column) => {
            if (column
                && (!columnName || columnName.indexOf(column) > -1)
                && !this.filterExcludeList.includes(column)
                && column !== '_filterset') {
                const meta = this.metaGet(column);
                let filterValue = value;
                let filterType = 'STRING';
                let filterParam01 = '';
                let filterParam02 = '';
                if (meta) {
                    filterType = meta.filterType || filterType;
                    filterParam01 = meta.filterParam01 || filterParam01;
                    filterParam02 = meta.filterParam02 || filterParam02;
                    filterValue = Reflect.has(meta, 'filterValue') ? meta.filterValue : filterValue;
                }
                if (filterType !== 'N/A') {
                    requestData.jsonRequest.griddata.filterdef.push({
                        columnName: column,
                        filterValue: filterValue,
                        filtertype: filterType,
                        param01: filterParam01,
                        param02: filterParam02
                    });
                }
            }
        });
        requestData.jsonRequest = JSON.stringify(requestData.jsonRequest);
        return DIContainer.getInstance('Request').PATCH({
            endpoint: 'filterdef',
            loadingMessage: 'Saving...',
            requestData: requestData
        }).pipe(
        // trigger our asterisk to disappear on filter toggles
        tap(() => {
            this.set('_filterset', this.get('_filterset'), 'load');
            this.clearFilterCache().subscribe();
        }));
    }
    loadFilters() {
        const requestData = {
            module: this.filterModule,
            filterSetName: this.get('_filterset')
        };
        return DIContainer.getInstance('Request').GET({
            endpoint: 'filterdef',
            action: 'list',
            requestData: requestData,
            cache: true
        }).pipe(tap((oResp) => {
            const filterData = oResp.payload?.result || [];
            if (this.get('_filterset').toLowerCase() === 'default' && !filterData.length) {
                forEachKey(this.defaultData, (val, key) => {
                    if (this.filterExcludeList.indexOf(key) === -1) {
                        filterData.push({ columnname: key, filtervalue: val });
                    }
                });
            }
            filterData.forEach((oFilter) => {
                this.set(oFilter.columnname, oFilter.filtervalue, 'load');
            });
        }));
    }
    clearFilterCache() {
        const requestData = {
            module: this.filterModule,
            filterSetName: this.get('_filterset')
        };
        return DIContainer.getInstance('Request').clearCache({
            endpoint: 'filterdef',
            action: 'list',
            requestData: requestData,
            method: 'GET'
        });
    }
    destroy() {
        // Complete all value subjects
        for (const prop in this.valueSubjects) {
            this.valueSubjects[prop].complete();
        }
        this.deleteChildGridRecordData();
        this._changeSubject = undefined;
        this.data = {};
        this._loadedData = {};
        this.changedData = {};
        this.defaultData = {};
        super.destroy();
    }
}
/** The version of the Record class */
DataRecord.VERSION = '$Revision: 129875 $';
